import { create } from "zustand";
import type {
  AnswerResult,
  DailyItem,
  DailySession,
  Game,
  ProfileId,
  CompletionReward,
} from "@/lib/types";
import { ApiError, api } from "@/lib/api";
import { todayKey } from "@/lib/date";
import { gradeLocally } from "@/lib/grade";
import {
  cacheGet,
  cacheSet,
  enqueue,
  sessionCacheKey,
  type CachedSession,
} from "@/lib/offline";
import { queueChanged, useSyncStore } from "./sync";

interface LoadedState {
  session: DailySession;
  idx: number;
  combo: number;
  lastResult: AnswerResult | null;
  lastItem: DailyItem | null;
  missed: Array<{ item: DailyItem; result: AnswerResult }>;
  done: boolean;
  reward: CompletionReward | null;
}

interface SessionState {
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  loaded: LoadedState | null;
  load: (profile: ProfileId, game: Game) => Promise<void>;
  answer: (profile: ProfileId, userAnswer: string) => Promise<AnswerResult | null>;
  advance: () => void;
  complete: (profile: ProfileId) => Promise<void>;
  reset: () => void;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  status: "idle",
  error: null,
  loaded: null,

  async load(profile, game) {
    set({ status: "loading", error: null });
    try {
      const session = await api.session.today(profile, game, todayKey());
      await cacheSet(sessionCacheKey(profile, game, todayKey()), session);
      const resumedIdx = Math.min(
        session.answered_count ?? 0,
        session.items.length,
      );
      const done =
        !!session.completed_at || resumedIdx >= session.items.length;
      set({
        status: "ready",
        error: null,
        loaded: {
          session,
          idx: done ? session.items.length : resumedIdx,
          combo: 0,
          lastResult: null,
          lastItem: null,
          missed: [],
          done,
          reward: null,
        },
      });
    } catch (err) {
      const cached = await cacheGet<CachedSession>(
        sessionCacheKey(profile, game, todayKey()),
      ).catch(() => null);
      if (cached) {
        const resumedIdx = Math.min(cached.answered_count ?? 0, cached.items.length);
        set({
          status: "ready",
          error: "Sin conexión · progreso pendiente de sincronizar",
          loaded: {
            session: cached,
            idx: resumedIdx,
            combo: 0,
            lastResult: null,
            lastItem: null,
            missed: [],
            done: !!cached.completed_at || resumedIdx >= cached.items.length,
            reward: null,
          },
        });
        return;
      }
      set({
        status: "error",
        error: err instanceof Error ? err.message : "failed to load",
      });
    }
  },

  async answer(profile, userAnswer) {
    const cur = get().loaded;
    if (!cur) return null;
    const item = cur.session.items[cur.idx];
    if (!item) return null;

    // Optimistic local grading — the UI gets an instant answer. The server
    // still runs the authoritative grade (it alone can fuzzy-match vocab and
    // generate an AI explanation); when its response arrives we reconcile.
    const local = gradeLocally(item, userAnswer);
    const nextCombo = local.correct ? cur.combo + 1 : 0;
    const optimistic: AnswerResult = {
      correct: local.correct,
      correct_answer: local.correctAnswer,
      user_answer: userAnswer,
      explanation: null,
      ai_adjudicated: false,
      streak: nextCombo,
      session_correct: cur.session.correct + (local.correct ? 1 : 0),
      session_total: cur.session.items.length,
    };
    const optimisticMissed = local.correct
      ? cur.missed
      : [...cur.missed, { item, result: optimistic }];

    set({
      loaded: {
        ...cur,
        combo: nextCombo,
        lastResult: optimistic,
        lastItem: item,
        missed: optimisticMissed,
        session: {
          ...cur.session,
          correct: optimistic.session_correct,
          best_streak: Math.max(cur.session.best_streak, nextCombo),
        },
      },
    });

    // Fire-and-await the authoritative grade. Reconcile if the server flipped
    // a wrong-looking answer to correct (fuzzy match / AI adjudication) or
    // enriched it with an explanation.
    const answerBody = {
        session_id: cur.session.session_id,
        item_id: item.id,
        user_answer: userAnswer,
        request_id: crypto.randomUUID(),
      };
    try {
      const server = await api.session.answer(profile, answerBody);
      const after = get().loaded;
      if (!after) return server;
      const missed = (() => {
        // Sync missed list with the server's verdict
        const base = after.missed.filter((m) => m.item.id !== item.id);
        return server.correct ? base : [...base, { item, result: server }];
      })();
      const isCurrent = after.lastItem?.id === item.id;
      set({
        loaded: {
          ...after,
          lastResult: isCurrent ? server : after.lastResult,
          missed,
          session: {
            ...after.session,
            correct: server.session_correct,
            best_streak: Math.max(
              after.session.best_streak,
              server.streak,
              after.combo,
            ),
          },
        },
      });
      return server;
    } catch (err) {
      const retryable = !(err instanceof ApiError) || err.status >= 500;
      if (retryable) {
        await enqueue({
          id: answerBody.request_id,
          kind: "session_answer",
          profileId: profile,
          body: answerBody,
          createdAt: Date.now(),
        });
        await queueChanged();
        set({ error: "Guardado en el teléfono · se sincronizará automáticamente" });
      } else {
        set({ error: err instanceof Error ? err.message : "answer failed" });
      }
      return optimistic;
    }
  },

  advance() {
    const cur = get().loaded;
    if (!cur) return;
    const nextIdx = cur.idx + 1;
    const done = nextIdx >= cur.session.items.length;
    set({
      loaded: {
        ...cur,
        idx: nextIdx,
        lastResult: null,
        lastItem: null,
        done,
      },
    });
    void cacheSet(
      sessionCacheKey(cur.session.profile_id, cur.session.game, cur.session.date_key),
      { ...cur.session, answered_count: nextIdx },
    );
  },

  async complete(profile) {
    const cur = get().loaded;
    if (!cur) return;
    try {
      await useSyncStore.getState().flush();
      const reward = await api.session.complete(profile, cur.session.session_id);
      set({
        loaded: {
          ...cur,
          done: true,
          reward,
          session: { ...cur.session, completed_at: Date.now() / 1000 },
        },
      });
      await cacheSet(
        sessionCacheKey(profile, cur.session.game, cur.session.date_key),
        { ...cur.session, completed_at: Date.now() / 1000, answered_count: cur.session.items.length },
      );
    } catch (err) {
      const retryable = !(err instanceof ApiError) || err.status >= 500;
      if (retryable) {
        await enqueue({
          id: `complete:${cur.session.session_id}`,
          kind: "session_complete",
          profileId: profile,
          body: { session_id: cur.session.session_id },
          createdAt: Date.now(),
        });
        await queueChanged();
        set({
          error: "Resultado guardado en el teléfono",
          loaded: {
            ...cur,
            done: true,
            session: { ...cur.session, completed_at: Date.now() / 1000 },
          },
        });
      } else {
        set({ error: err instanceof Error ? err.message : "complete failed" });
      }
    }
  },

  reset() {
    set({ status: "idle", error: null, loaded: null });
  },
}));
